import { useEffect, useState } from 'react';
import toast, { Toaster, ToastBar, Toast } from 'react-hot-toast';
import { Outlet, useLocation } from 'react-router-dom';

import { LogoSpinner } from 'us-react-components/dist/LogoSpinner/LogoSpinner';
import { onEscapeAction } from 'us-react-components/dist/utils/a11y';
import { createEnvironment, DependenciesProvider, Environment } from './dependencies';
import Header from './components/Header';
import Footer from './components/Footer';
import { Storage, ToastTiming } from './constants';
import { ModalProvider } from './modals/modalContext';

const App = (): JSX.Element | null => {
  const [dependencies, setDependencies] = useState<Environment | undefined>();
  const [isLoaded, setIsLoaded] = useState(false);
  const [previousPath, setPreviousPath] = useState('');

  const location = useLocation();

  useEffect(() => {
    const createEnv = async (): Promise<void> => {
      try {
        const env = await createEnvironment();

        await env.setup();
        setDependencies(env);
        setIsLoaded(true);
      } catch (e) {
        console.warn(e);
        setTimeout(() => {
          const MAX_RETRIES = 2;
          try {
            const retryCount = sessionStorage.getItem('loadRetryCount') || 0;

            if (+retryCount >= MAX_RETRIES) {
              // user logged out with no refresh token, or persistent JS errors preventing app load
              localStorage.removeItem(Storage.US_SITTER_OBJECT);
              localStorage.removeItem(Storage.US_USER_OBJECT);
              localStorage.removeItem(Storage.US_USER_AUTH_TOKEN);
              localStorage.removeItem(Storage.US_USER_REFRESH_TOKEN);
              sessionStorage.removeItem('loadRetryCount');
              window.location.href = '/';
            } else {
              sessionStorage.setItem('loadRetryCount', `${+retryCount + 1}`);
              window.location.reload();
            }
          } catch (timeoutError) {
            console.warn(timeoutError);
            window.location.href = '/';
          }
        }, 5000);
      }
    };

    createEnv();
  }, []);

  useEffect(() => {
    const NAV_BAR_HEIGHT = 80;

    if (window.scrollY && window.scrollY > NAV_BAR_HEIGHT) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const currentPath = location.pathname.split('/').pop();
    dependencies?.services.tracker.track(`viewed ${currentPath}`, { previous_screen: previousPath });
    dependencies?.services.tracker.pageview(location.pathname + location.search);
    setPreviousPath(currentPath!);
  }, [location]);

  if (!dependencies || !isLoaded) {
    return (
      <div style={{ margin: '25px auto', textAlign: 'center' }}>
        <LogoSpinner style={{ marginBottom: 2, height: 40, width: 40 }} />
        <div style={{ color: '#565656', marginTop: 10 }}>Loading...</div>
      </div>
    );
  }

  return (
    <DependenciesProvider value={dependencies}>
      <Toaster
        containerStyle={{ marginTop: 40 }}
        position='top-center'
        toastOptions={{
          error: {
            duration: ToastTiming.DISPLAY_TIME_ERROR,
          },
          success: {
            duration: ToastTiming.DISPLAY_TIME_SUCCESS,
          },
        }}
      >
        {(t: Toast): JSX.Element => (
          <div
            role='button'
            tabIndex={0}
            onClick={(): void => toast.dismiss(t.id)}
            onKeyDown={(e): void => onEscapeAction(e, (): void => toast.dismiss(t.id))}
          >
            <ToastBar toast={t} />
          </div>
        )}
      </Toaster>
      <ModalProvider>
        <Header />
        <main>
          <Outlet />
        </main>
        <Footer />
      </ModalProvider>
    </DependenciesProvider>
  );
};

export default App;
